/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency';
import TransactionalMessageModel from 'cio/models/transactional-message';
import RouterService from '@ember/routing/router-service';
import MetricsService from 'cio/services/metrics';
import EnvironmentService from 'cio/services/environment';
import TagModel from 'cio/models/tag';
import { Measure, Entities, Periods } from 'cio/utils/metrics/common';
import { pct } from 'cio/helpers/pct';
import { Metric } from 'cio/utils/metrics/models/metric';
import clamp from 'clamp';
import { isDeliveryMeasureEnabledForChannel } from 'cio/utils/metrics/measure';

interface TransactionalMessageListItemArgs {
  message: TransactionalMessageModel;
  onDeleteMessage: () => unknown;
  reloadMessageModel: () => unknown;
  onTagClicked: (tag: TagModel) => unknown;
}

interface MetricCell {
  metric: Measure;
  label: string;
  labelTooltipContent: string;
  value: string | number;
  valueClass?: string;
}

export default class TransactionalMessageListItem extends Component<TransactionalMessageListItemArgs> {
  @service router!: RouterService;
  @service metrics!: MetricsService;
  @service environment!: EnvironmentService;

  @tracked metric?: Metric;

  get isHidden() {
    return this.args.message.isDefault && (!this.metric?.sent?.allTimeTotal || this.metric?.sent?.allTimeTotal === 0);
  }

  @action
  clampDescription(element: HTMLTableRowElement) {
    let descriptionElem = element.querySelector('.js-transactional-message-description');
    if (descriptionElem) {
      clamp(descriptionElem, { clamp: 2, useNativeClamp: false });
    }
  }

  metricTask = restartableTask(async () => {
    this.metric = await this.metrics.fetchMetric(
      Entities.TRANSACTIONAL,
      this.args.message.id,
      Periods.DAYS,
      null,
      this.args.message.type,
      {
        environmentId: this.environment.current.id,
      },
    );
  });

  get metricCells(): MetricCell[] {
    return (
      [
        {
          metric: 'sent',
          label: 'Sent',
          labelTooltipContent: 'Total messages sent',
          value: this.metric?.sent?.allTimeTotal ?? 0,
        },
        {
          metric: 'delivered',
          label: 'Delivered',
          labelTooltipContent: 'Average delivery rate',
          value: pct([this.metric?.delivered?.allTimeRate]),
        },
        {
          metric: 'opened',
          label: 'Opened',
          labelTooltipContent: 'Average open rate',
          value: pct([this.metric?.opened?.allTimeRate]),
        },
        {
          metric: 'clicked',
          label: 'Clicked',
          labelTooltipContent: 'Average click rate',
          value: pct([this.metric?.clicked?.allTimeRate]),
        },
      ] as MetricCell[]
    ).filter((cell) => isDeliveryMeasureEnabledForChannel(cell.metric, this.args.message.type));
  }
}
